import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'
import * as Turbo from '@hotwired/turbo'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

class ScheduleCalendarController extends Controller {
  static targets = ['panel']
  static values = {
    currentDate: String,
    calendarData: Object,
    dataUrl: String,
    baseUrl: { type: String, default: '/admin/schedules' },
    viewYear: Number,
    viewMonth: Number,
    open: Boolean,
    sidebar: Boolean
  }

  connect () {
    const d = new Date(this.currentDateValue + 'T00:00:00')
    this.viewYearValue = d.getFullYear()
    this.viewMonthValue = d.getMonth() + 1
    this.cache = {}
    this.cacheKey(this.viewYearValue, this.viewMonthValue, this.calendarDataValue)
    this.render()

    if (!this.sidebarValue) {
      this.clickOutsideHandler = (e) => this.clickOutside(e)
      document.addEventListener('click', this.clickOutsideHandler)
    }
  }

  disconnect () {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
    }
    if (this.loadAbort) this.loadAbort.abort()
  }

  toggle (e) {
    if (this.sidebarValue) return
    e.stopPropagation()
    this.openValue = !this.openValue
    this.panelTarget.classList.toggle('hidden', !this.openValue)
  }

  clickOutside (e) {
    if (!this.openValue) return
    if (this.element.contains(e.target)) return
    this.openValue = false
    this.panelTarget.classList.add('hidden')
  }

  prevMonth () {
    this.viewMonthValue--
    if (this.viewMonthValue < 1) {
      this.viewMonthValue = 12
      this.viewYearValue--
    }
    this.loadAndRender()
  }

  nextMonth () {
    this.viewMonthValue++
    if (this.viewMonthValue > 12) {
      this.viewMonthValue = 1
      this.viewYearValue++
    }
    this.loadAndRender()
  }

  async loadAndRender () {
    const key = `${this.viewYearValue}-${this.viewMonthValue}`
    if (this.cache[key]) {
      this.render()
      return
    }

    if (this.loadAbort) this.loadAbort.abort()
    this.loadAbort = new AbortController()

    const url = `${this.dataUrlValue}?year=${this.viewYearValue}&month=${this.viewMonthValue}`
    try {
      const resp = await fetch(url, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.loadAbort.signal
      })
      const data = await resp.json()
      this.cacheKey(this.viewYearValue, this.viewMonthValue, data)
    } catch (e) {
      if (e.name === 'AbortError') return
    }
    this.render()
  }

  cacheKey (year, month, data) {
    this.cache[`${year}-${month}`] = data
  }

  getData () {
    return this.cache[`${this.viewYearValue}-${this.viewMonthValue}`] || {}
  }

  selectDay (e) {
    const id = e.currentTarget.dataset.scheduleId
    if (!id) return
    Turbo.visit(`${this.baseUrlValue}/${id}`)
  }

  render () {
    const data = this.getData()
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const currentDate = new Date(this.currentDateValue + 'T00:00:00')

    const year = this.viewYearValue
    const month = this.viewMonthValue

    // First day of month (0=Sun ... 6=Sat) → shift to Monday-based
    const firstDay = new Date(year, month - 1, 1)
    let startWeekday = firstDay.getDay() - 1
    if (startWeekday < 0) startWeekday = 6
    const daysInMonth = new Date(year, month, 0).getDate()

    const sidebarClass = this.sidebarValue ? ' schedule-calendar--sidebar' : ''
    let html = `<div class="schedule-calendar${sidebarClass}">`

    // Header
    html += '<div class="schedule-calendar__header">'
    html += '<button type="button" class="schedule-calendar__nav" data-action="schedule-calendar#prevMonth">&lsaquo;</button>'
    html += `<span class="schedule-calendar__title">${MONTH_NAMES[month - 1]} ${year}</span>`
    html += '<button type="button" class="schedule-calendar__nav" data-action="schedule-calendar#nextMonth">&rsaquo;</button>'
    html += '</div>'

    // Weekdays
    html += '<div class="schedule-calendar__weekdays">'
    WEEKDAYS.forEach(d => {
      html += `<span class="schedule-calendar__weekday">${d}</span>`
    })
    html += '</div>'

    // Days grid
    html += '<div class="schedule-calendar__days">'

    // Empty cells before first day
    for (let i = 0; i < startWeekday; i++) {
      html += '<span class="schedule-calendar__day schedule-calendar__day--empty"></span>'
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const entry = data[dateStr]
      const dateObj = new Date(dateStr + 'T00:00:00')
      const isToday = dateObj.getTime() === today.getTime()
      const isCurrent = dateObj.getTime() === currentDate.getTime()
      const hasSchedule = !!entry
      const hasAppointments = entry && entry.has_appointments

      const classes = ['schedule-calendar__day']
      if (!hasSchedule) classes.push('schedule-calendar__day--disabled')
      if (hasSchedule) classes.push('schedule-calendar__day--clickable')
      if (hasAppointments && !isCurrent) classes.push('schedule-calendar__day--has-appointments')
      if (isToday) classes.push('schedule-calendar__day--today')
      if (isCurrent) classes.push('schedule-calendar__day--current')

      let dotHtml = ''
      if (hasAppointments) {
        dotHtml = '<span class="schedule-calendar__dot schedule-calendar__dot--appointment"></span>'
      } else if (hasSchedule) {
        dotHtml = '<span class="schedule-calendar__dot schedule-calendar__dot--schedule"></span>'
      }

      if (hasSchedule) {
        html += `<button type="button" class="${classes.join(' ')}" data-schedule-id="${entry.id}" data-action="schedule-calendar#selectDay">${day}${dotHtml}</button>`
      } else {
        html += `<span class="${classes.join(' ')}">${day}</span>`
      }
    }

    html += '</div></div>'

    this.panelTarget.innerHTML = html
  }
}

stimulus.register('schedule-calendar', ScheduleCalendarController)
